import { DealerIdPresentation } from '@ph-core/enums/dealer-id-presentation.enum';
import type { Environment } from '@ph-env/model/environment.model';
import type { DealerInfo } from '@ph-model/login';

/** Use with `Environment` or any object that exposes `environment` (e.g. `ConfigService`). */
export type DealerIdPresentationSource =
  | Pick<Environment, 'features'>
  | { readonly environment: Environment };

type DealerIdFieldSource = {
  cmsDealerNumber?: string | null;
  externalDealershipId?: string | null;
  dealerInfo?: DealerInfo | null;
};

export function dealerIdPresentationPreference(source: DealerIdPresentationSource): DealerIdPresentation {
  return 'environment' in source
    ? source.environment.features.dealerIdForPresentation
    : source.features.dealerIdForPresentation;
}

export function isExternalPresentation(source: DealerIdPresentationSource): boolean {
  return dealerIdPresentationPreference(source) === DealerIdPresentation.EXTERNAL;
}

export function getDisplayDealerId(
  source: unknown,
  preference: DealerIdPresentation,
): string {
  if (source == null || typeof source !== 'object') {
    return '';
  }

  const record = source as DealerIdFieldSource;
  if (!('cmsDealerNumber' in record || 'dealerInfo' in record || 'externalDealershipId' in record)) {
    return '';
  }

  const { cms, external } = extractDealerIdFields(record);
  return resolveDisplayDealerId(preference, cms, external);
}

export function resolveDisplayDealerId(
  preference: DealerIdPresentation,
  cmsDealerNumber: string | null | undefined,
  externalDealershipId: string | null | undefined,
): string {
  const external = normalizeId(externalDealershipId);
  const internal = normalizeId(cmsDealerNumber);

  if (preference === DealerIdPresentation.EXTERNAL) {
    return external || internal;
  }

  return internal || external;
}

function extractDealerIdFields(source: DealerIdFieldSource): {
  cms: string | null | undefined;
  external: string | null | undefined;
} {
  const cms = 'cmsDealerNumber' in source ? source.cmsDealerNumber : undefined;
  const dealerInfo = source.dealerInfo;

  if (dealerInfo != null && typeof dealerInfo === 'object') {
    return { cms, external: dealerInfo.externalDealershipId };
  }

  const external = 'externalDealershipId' in source ? source.externalDealershipId : undefined;
  return { cms, external };
}

function normalizeId(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}
